import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import styles from './youtube-player.module.scss';
import classNames from 'classnames/bind';
import thumbnailDummy from './thumbnail-dummy.jpg';
// Import the SVG icons
import IconPlay from './icon-play-youtube.svg';
import IconPause from './icon-pause-youtube.svg';
import IconReplay from './icon-replay-youtube.svg';
const cn = classNames.bind(styles);

function YoutubePlayer({
  startTime = 0,
  endTime = 0,
  url = 'https://www.youtube.com/watch?v=-eEvfjMQzBA',
  thumbnail,
  onEnded,
  pauseVideo = false,
  disableSeek = false,
  autoplay = true,
  earlyEndTriggerSeconds = 5,
}) {
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [ended, setEnded] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true); // 기본적으로 컨트롤 바 표시
  const [loading, setLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [initialState, setInitialState] = useState(true);
  const [userActivity, setUserActivity] = useState(false); // 사용자 활동 추적
  const playerRef = useRef(null);
  const playerWrapperRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const [clickCount, setClickCount] = useState(0);
  const clickTimeoutRef = useRef(null);
  const [hasTriggeredEarlyEnd, setHasTriggeredEarlyEnd] = useState(false);
  const [autoPlayHandled, setAutoPlayHandled] = useState(false);
  const prevPauseVideoRef = useRef(pauseVideo);

  // 비디오 ID를 가져와 썸네일 생성에 사용
  const getVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const videoId = getVideoId(url);
  const thumbnailUrl =
    thumbnail || (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '');  

  const handlePlayPause = () => {
    if (ended) {
      setEnded(false);
      void playerRef.current?.seekTo(startTime);
      setHasTriggeredEarlyEnd(false); // 트리거 상태 재설정
      return setPlaying(true);
    }

    // 처음 재생 버튼을 클릭할 때 이 상태를 기록
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true);
    }

    // 초기 상태이고 재생하지 않은 경우, 초기 상태에서 벗어남
    if (initialState && !playing) {
      setInitialState(false);
    }

    // 매번 재생/일시 정지 전환 시 컨트롤 바 표시
    setShowControls(true);

    // 일시 정지인 경우, 컨트롤 바를 계속 표시
    if (playing) {
      // 일시 정지하려는 경우, 컨트롤 바를 숨기는 타이머를 제거
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    } else {
      // 재생하려는 경우, 컨트롤 바를 숨기는 타이머 설정
      startHideControlsTimer();
    }

    setPlaying(!playing);
    // 재생 시작할 때 트리거 상태 재설정
    if (!playing) {
      setHasTriggeredEarlyEnd(false);
    }
  };

  const handleEnded = () => {
    if (!loop) {
      setEnded(true);
      setPlaying(false);
      void playerRef.current?.seekTo(startTime);
      setShowControls(true);
    }
    // 미리 트리거되지 않은 경우에만 onEnded 실행
    if (!hasTriggeredEarlyEnd && onEnded) {
      onEnded();
    }
  };

  const handleProgress = (state) => {
    const { playedSeconds, played } = state;

    // 진행 막대 계산 로직 수정, startTime과 endTime 고려
    const effectiveStartTime = startTime || 0;
    const effectiveEndTime = endTime > 0 ? endTime : duration;
    const effectiveDuration = effectiveEndTime - effectiveStartTime;

    // 유효 구간에 대한 상대적 진행률 백분율 계산
    const currentPosition = playedSeconds - effectiveStartTime;
    const progressPercentage =
      effectiveDuration > 0 ? (currentPosition / effectiveDuration) * 100 : 0;

    // 진행률을 0-100 범위로 제한
    const clampedProgress = Math.max(0, Math.min(100, progressPercentage));
    setProgress(clampedProgress);

    // 남은 시간 계산
    const remainingTime = effectiveEndTime - playedSeconds;

    // 남은 시간이 earlyEndTriggerSeconds초 이하이고 이전에 트리거되지 않았을 때 실행
    if (remainingTime <= earlyEndTriggerSeconds && remainingTime > 0 && !hasTriggeredEarlyEnd) {
      if (onEnded) {
        onEnded();
      }
      setHasTriggeredEarlyEnd(true);
    }

    // 재생 진행이 있으면 비디오가 준비되었고 재생된 것임
    if (played > 0) {
      // 비디오가 준비된 상태로 표시되도록 확인
      if (!videoReady) {
        setVideoReady(true);
      }

      // 여전히 로딩 상태라면 해제
      if (loading) {
        setLoading(false);
      }

      // 버퍼링 상태지만 재생 진행이 있다면 버퍼링 상태 해제
      if (buffering) {
        setBuffering(false);
      }

      // 초기 상태이고 재생 진행이 있다면 초기 상태에서 벗어남
      if (initialState) {
        setInitialState(false);
      }
    }

    if (endTime && playedSeconds >= endTime) {
      handleEnded();
    }
  };

  const handleSeek = (e) => {
    // If seeking is disabled, don't proceed
    if (disableSeek) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percentage = offsetX / rect.width;

    // 클릭 위치에 따라 목표 시간 계산
    const effectiveStartTime = startTime || 0;
    const effectiveEndTime = endTime > 0 ? endTime : duration;

    // 목표 시간이 유효 범위 내에 있는지 확인
    const targetTime =
      effectiveStartTime + percentage * (effectiveEndTime - effectiveStartTime);

    // 목표 시간이 유효 범위 내에 있는지 확인
    const clampedTargetTime = Math.max(
      effectiveStartTime,
      Math.min(effectiveEndTime, targetTime)
    );

    void playerRef.current?.seekTo(clampedTargetTime);

    // 동일한 진행률 백분율 계산 방식 사용
    const progressPercentage = percentage * 100;
    setProgress(Math.max(0, Math.min(100, progressPercentage)));

    // 매번 진행 막대를 드래그할 때 컨트롤 바 표시 재설정
    setShowControls(true);
    startHideControlsTimer();
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  // 컨트롤 바를 숨기는 타이머 시작
  const startHideControlsTimer = () => {
    // initialState 검사 제거
    if (playing) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      const hideDelay = 1000;
      controlsTimeoutRef.current = setTimeout(() => {
        if (playing && !userActivity) {
          setShowControls(false);
        }
      }, hideDelay);
    }
  };

  // 사용자 마우스 이동 시 트리거
  const handleUserActivity = () => {
    setUserActivity(true);
    setShowControls(true);

    // 컨트롤 바 타이머 재설정
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    activityTimeoutRef.current = setTimeout(() => {
      setUserActivity(false);
      if (playing) {
        startHideControlsTimer();
      }
    }, 1000); // 1초 후 활동 없으면 재설정
  };

  const handleLoopToggle = (e) => {
    e.stopPropagation(); // 이벤트 버블링을 덮개로 방지
    setLoop(!loop);
    // 컨트롤 바 타이머 재설정
    handleUserActivity();
  };

  const handleReady = () => {
    setVideoReady(true);
    setLoading(false);

    // 자동 재생 시 재생 상태 업데이트
    if (playerRef.current?.props.playing) {
      setPlaying(true);
      startHideControlsTimer();
    }

    // 비디오가 준비되면 컨트롤 바 표시
    setShowControls(true);

    // 비디오가 준비되고 재생 상태인 경우 컨트롤 바 숨김 타이머 설정
    if (playing) {
      startHideControlsTimer();
    }
  };

  const handleBuffer = () => {
    setBuffering(true);
    // 버퍼링 시 컨트롤 바 표시
    setShowControls(true);
  };

  const handleBufferEnd = () => {
    setBuffering(false);
    // 버퍼링이 끝나고 재생 상태이면 컨트롤 바 숨김 타이머 재시작
    if (playing) {
      startHideControlsTimer();
    }
  };

  const handleError = (error) => {
    console.error('YouTube Player Error:', error);
    setLoading(false);
    // 오류 발생 시 컨트롤 바 표시
    setShowControls(true);
  };

  // 재생 상태 변경 시 컨트롤 바 처리
  useEffect(() => {
    if (playing) {
      // 1초 타임아웃 시간 통일 사용
      const hideDelay = 1000;

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      controlsTimeoutRef.current = setTimeout(() => {
        if (playing && !userActivity) {
          setShowControls(false);
        }
      }, hideDelay);
    } else {
      // 일시 정지 시 컨트롤 바 표시 및 숨김 타이머 제거
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [playing, userActivity]);

  useEffect(() => {
    // 5초 후에도 로딩 상태면 강제로 비로딩 상태로 설정
    // 이는 일부 경우에 YouTube API가 onReady 이벤트를 제대로 발생시키지 않는 것을 방지하기 위함
    const forceLoadingTimeout = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setVideoReady(true);
      }
    }, 5000);

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
      clearTimeout(forceLoadingTimeout);
    };
  }, [loading]);

  // 로딩 중 상태인지 확인
  const isLoading = loading && !videoReady;

  // 썸네일 표시 로직 수정
  // 1. 초기 상태에서 표시(재생되지 않았거나 비디오가 준비되지 않았으나 초기 상태 플래그가 true)
  // 2. 버퍼링 중에는 표시하지 않음(일시 정지 후 재생 재개 시 짧게 표시되는 문제 해결)
  // 3. 브라우저가 정책으로 자동 재생을 차단할 수 있는 경우 고려
  const shouldShowThumbnail =
    initialState && (!hasPlayedOnce || !videoReady || !playing) && !buffering;

  // 로딩 인디케이터를 표시해야 하는지 확인
  const shouldShowLoadingIndicator = isLoading || buffering;

  // 터치 장치 감지 추가
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // 터치 장치 감지
  useEffect(() => {
    const checkTouchDevice = () => {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };
    setIsTouchDevice(checkTouchDevice());
  }, []);

  // 클릭 이벤트 처리, 더블 클릭 감지에 사용
  const handleClick = (e) => {
    // 클릭 처리 간소화, 이제 재생/일시 정지만 처리
    handlePlayPause();
  };

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  // 자동 재생 처리 로직 수정
  useEffect(() => {
    if (playerRef.current && !autoPlayHandled) {
      // Only set to playing if autoplay is enabled
      if (autoplay) {
        setPlaying(true);
        startHideControlsTimer();
        // 컨트롤 바 숨김 즉시 트리거
        controlsTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 1000);
      }
      setAutoPlayHandled(true);
      // initialState를 즉시 false로 설정하지 않고 비디오가 실제로 재생될 때 설정
    }
  }, [autoPlayHandled, autoplay]);

  // handleStart 처리 함수 수정 - 이 함수는 비디오가 실제로 재생 시작될 때 트리거됨
  const handleStart = () => {
    setPlaying(true);
    setInitialState(false); // 비디오가 실제로 재생을 시작할 때만 initialState를 false로 설정
    setHasPlayedOnce(true);
    setUserActivity(false); // 사용자 활동 상태가 false인지 확인
    startHideControlsTimer();
  };

  // pauseVideo 속성 변화 모니터링
  useEffect(() => {
    // pauseVideo가 변경됐는지 확인
    if (prevPauseVideoRef.current !== pauseVideo) {
      if (pauseVideo) {
        // 비디오 일시 정지
        setPlaying(false);
        // 컨트롤 바 표시
        setShowControls(true);
        // 컨트롤 바를 숨기는 타이머 제거
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      } else {
        // 비디오가 준비되고 종료 상태가 아닌 경우에만 재생 재개
        if (videoReady && !ended) {
          setPlaying(true);
          // 컨트롤 바를 숨기는 타이머 설정
          startHideControlsTimer();
        }
      }
      // 다음 비교를 위해 ref 값 업데이트
      prevPauseVideoRef.current = pauseVideo;
    }
  }, [pauseVideo, videoReady, ended]);

  // 키보드 이벤트 처리
  const handleKeyDown = (e) => {
    // 스페이스 키로 재생/일시 정지 제어
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      handlePlayPause();
    }

    // 좌우 방향 키로 진행 상태 제어, seek 기능이 비활성화되지 않은 경우에만 처리
    if (!disableSeek) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const newTime = Math.min(
          endTime > 0 ? endTime : duration,
          playerRef.current.getCurrentTime() + 5
        );
        void playerRef.current?.seekTo(newTime);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const newTime = Math.max(
          startTime,
          playerRef.current.getCurrentTime() - 5
        );
        void playerRef.current?.seekTo(newTime);
      }
    }
  };

  const handleTouchStart = () => {
    handleUserActivity();
  };

  const handleTouchMove = () => {
    handleUserActivity();
  };

  const handleTouchEnd = () => {
    // 터치 종료 후 컨트롤 바 숨김 타이머 시작
    if (playing) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (playing && !userActivity) {
          setShowControls(false);
        }
      }, 1000); // 1초 후 숨김
    }
  };

  const handleMouseEnter = () => {
    setShowControls(true);
    setUserActivity(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  };

  const handleMouseLeave = () => {
    setUserActivity(false);
    if (playing) {
      startHideControlsTimer();
    }
  };

  const handleMouseMove = () => {
    handleUserActivity();
  };

  return (
    <div
      className={cn('videoWrapper', {
      })}
      ref={playerWrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onKeyDown={handleKeyDown}
      tabIndex="0"
    >
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={playing}
        className={styles.reactPlayer}
        loop={loop}
        width="100%"
        height="100%"
        onEnded={handleEnded}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onReady={handleReady}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        onError={handleError}
        playsinline
        onStart={handleStart}
        config={{
          youtube: {
            playerVars: {
              autoplay: autoplay ? 1 : 0,
              controls: 0,
              showinfo: 0,
              rel: 0,
              iv_load_policy: 3,
              modestbranding: 0, // YouTube 브랜드 숨김
              playsinline: 1,
              enablejsapi: 1,
              fs: 1,
              color: 'white',
              disablekb: 1,
              start: startTime,
            },
            embedOptions: {
              autoplay: autoplay ? 1 : 0,
              rel: 0,
              modestbranding: 0, // 숨김
            },
          },
        }}
      />

      {/* 썸네일 오버레이, 초기 상태 또는 버퍼링 중에만 표시 */}
      {shouldShowThumbnail && (
        <div
          className={cn('thumbnailOverlay', {
            loading: shouldShowLoadingIndicator,
          })}
          style={{ backgroundImage: `url(${thumbnailUrl})` }}
          onClick={(e) => {
            // 수정: handleClick 호출 제거, 컨트롤 바 표시만 처리
            handleUserActivity();
            e.stopPropagation();
          }}
        >
          {shouldShowLoadingIndicator && (
            <div className={styles.loadingIndicator}>
              <div className={styles.loadingSpinner}></div>
            </div>
          )}
        </div>
      )}

      {/* 투명 오버레이, 모든 클릭 이벤트 포착 */}
      <div
        className={cn('controlsOverlay', { visible: showControls })}
        onClick={(e) => {
          // 수정: handleClick 호출 제거, 컨트롤 바 표시만 처리
          handleUserActivity();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          handleUserActivity();
        }}
        tabIndex="0"
        role="button"
        aria-label={playing ? 'Pause video' : 'Play video'}
        onKeyDown={(e) => e.key === 'Enter' && handlePlayPause()}
      >
        {/* Play/Pause 버튼 중앙에 배치 */}
        <button
          className={cn('playPauseButton', {
            visible: showControls || (!playing && videoReady),
          })}
          onClick={(e) => {
            e.stopPropagation(); // 이벤트 버블링을 덮개로 방지
            handlePlayPause();
          }}
          onTouchStart={(e) => {
            e.stopPropagation(); // 이벤트 버블링을 덮개로 방지
            handleUserActivity();
          }}
          aria-label={playing ? 'Pause' : ended ? 'Replay' : 'Play'}
          tabIndex="0"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              handlePlayPause();
            }
          }}
        >
          {/* Use imported SVG icons based on player state */}
          {ended && !loop ? (
            <img src={IconReplay} alt="Replay" className={styles.replayIcon} />
          ) : playing ? (
            <img src={IconPause} alt="Pause" className={styles.pauseIcon} />
          ) : (
            <img src={IconPlay} alt="Play" className={styles.playIcon} />
          )}
        </button>

        {/* 하단 진행 막대 */}
        <div
          className={cn('progressBarContainer', { visible: showControls })}
          onClick={(e) => e.stopPropagation()} // 이벤트 버블링을 덮개로 방지
        >
          <div
            className={cn('progressBar', { disabled: disableSeek })}
            onClick={(e) => {
              e.stopPropagation(); // 이벤트 버블링을 덮개로 방지
              if (!disableSeek) {
                handleSeek(e);
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation(); // 이벤트 버블링을 덮개로 방지
              
              // Skip if seeking is disabled
              if (disableSeek) {
                handleUserActivity();
                return;
              }
              
              // 터치 위치를 가져와 seek 이벤트로 변환
              const touch = e.touches[0];
              const progressBar = e.currentTarget;
              const rect = progressBar.getBoundingClientRect();
              const offsetX = touch.clientX - rect.left;
              const percentage = offsetX / rect.width;

              // 목표 시간이 유효 범위 내에 있는지 확인
              const effectiveStartTime = startTime || 0;
              const effectiveEndTime = endTime > 0 ? endTime : duration;

              // 목표 시간이 유효 범위 내에 있는지 확인
              const targetTime =
                effectiveStartTime +
                percentage * (effectiveEndTime - effectiveStartTime);

              // 목표 시간이 유효 범위 내에 있는지 확인
              const clampedTargetTime = Math.max(
                effectiveStartTime,
                Math.min(effectiveEndTime, targetTime)
              );

              void playerRef.current?.seekTo(clampedTargetTime);

              // 진행률 설정 및 0-100 범위 내에 있는지 확인
              const progressPercentage = percentage * 100;
              setProgress(Math.max(0, Math.min(100, progressPercentage)));

              setShowControls(true);
              handleUserActivity();
            }}
            onKeyDown={(e) => {
              e.stopPropagation(); // 이벤트 버블링을 덮개로 방지
              
              // Skip if seeking is disabled
              if (disableSeek) return;
              
              if (e.key === 'ArrowRight') {
                const newTime = Math.min(
                  endTime > 0 ? endTime : duration,
                  playerRef.current.getCurrentTime() + 5
                );
                if (playerRef.current) {
                  void playerRef.current.seekTo(newTime);
                }
              } else if (e.key === 'ArrowLeft') {
                const newTime = Math.max(
                  startTime,
                  playerRef.current.getCurrentTime() - 5
                );
                if (playerRef.current) {
                  void playerRef.current.seekTo(newTime);
                }
              }
            }}
            tabIndex="0"
            role="slider"
            aria-label="Video progress"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={progress}
          >
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default YoutubePlayer;
